"use client";

import { c as _c } from "react/compiler-runtime";
import { Button } from "@local/ui/src/components/ui/Button";
import { ShoppingCartIcon } from "./ShoppingCartIcon";
import { CallToAction, getInitialCtaString } from "./CallToAction";
import { getT } from "@local/i18n/src/t";
import { getLanguageForMarket } from "@local/i18n/src/languages";
import { buy } from "@local/i18n/src/catalogs/buy";
import { Suspense, useMemo } from "react";
import { Link } from "@local/ui/src/components/ui/Link";
import { useSearchParams } from "next/navigation";
import { trackAction } from "@local/tracking/src/trackActions";
import { rebuildUrlParams, stripForcedTrackingParams } from "@local/utils/src/rebuildUrlParams";
import type { Offer } from "../../../types";
interface Props {
  callToAction?: string | null;
  isMirror?: boolean;
  noIcon?: boolean;
  skipView?: boolean;
  bigButton?: boolean;
  market: string;
  channel: string;
  mirrorUrl?: string;
  product?: Offer.PinProduct;
  prices?: Offer.ListingPrices;
}
export interface BtnBuyProps extends Props {
  full?: boolean;
  callToActionId?: string;
}
interface CtaProps {
  callToAction?: string | null;
  discount?: number;
  market: string;
}
const Cta = t0 => {
  const $ = _c(17);
  const {
    callToAction,
    discount,
    market
  } = t0;
  let T0;
  let actionLabel;
  let t1;
  if ($[0] !== callToAction || $[1] !== discount || $[2] !== market) {
    actionLabel = getT(getLanguageForMarket(market), {
      buy
    })("buy.selectSizeNow");
    T0 = Suspense;
    t1 = getInitialCtaString(callToAction, actionLabel, discount, market);
    $[0] = callToAction;
    $[1] = discount;
    $[2] = market;
    $[3] = T0;
    $[4] = actionLabel;
    $[5] = t1;
  } else {
    T0 = $[3];
    actionLabel = $[4];
    t1 = $[5];
  }
  let t2;
  if ($[6] !== t1) {
    t2 = <>{t1}</>;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  let t3;
  if ($[8] !== actionLabel || $[9] !== callToAction || $[10] !== discount || $[11] !== market) {
    t3 = <CallToAction input={callToAction} action={actionLabel} discount={discount} market={market} />;
    $[8] = actionLabel;
    $[9] = callToAction;
    $[10] = discount;
    $[11] = market;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  let t4;
  if ($[13] !== T0 || $[14] !== t2 || $[15] !== t3) {
    t4 = <T0 fallback={t2}>{t3}</T0>;
    $[13] = T0;
    $[14] = t2;
    $[15] = t3;
    $[16] = t4;
  } else {
    t4 = $[16];
  }
  return t4;
};
const BtnBuyInner = t0 => {
  const $ = _c(65);
  const {
    callToAction,
    full,
    bigButton,
    market,
    channel,
    mirrorUrl,
    product,
    prices,
    callToActionId
  } = t0;
  const searchParams = useSearchParams();
  let t1;
  if ($[0] !== searchParams) {
    t1 = searchParams?.get("item_id");
    $[0] = searchParams;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const itemId = t1;
  let t2;
  bb0: {
    if (!product) {
      t2 = undefined;
      break bb0;
    }
    const originalPrice = prices?.[0]?.value ?? product.msrp;
    const rawDiscount = prices?.[0]?.discountValue;
    const defaultDiscountPrice = rawDiscount && rawDiscount > 0 ? rawDiscount : originalPrice;
    let matchedModifierPrice;
    let hasSpecificSkuMatch = false;
    if ($[2] !== itemId || $[3] !== prices || $[4] !== product.skuLists || $[5] !== product.skus || $[6] !== product.type) {
      const allSkus = product.type === "BUNDLE" ? product.skuLists.flatMap(_temp) : product.skus;
      const matchingSku = itemId ? allSkus.find(sku => sku?.legacyId === itemId || sku.attributes.some(attr => attr.name === "payloadId" && attr.value === itemId) || sku.id === itemId || sku.name === itemId) : allSkus.find(_temp2);
      if (matchingSku) {
        hasSpecificSkuMatch = true;
        if (matchingSku.name && prices?.[0]?.modifiers?.length) {
          const matchedModifier = prices[0].modifiers.find(modifier => modifier.skus.includes(matchingSku.name as string));
          if (matchedModifier) {
            matchedModifierPrice = matchedModifier.amount;
          }
        }
      }
      $[2] = itemId;
      $[3] = prices;
      $[4] = product.skuLists;
      $[5] = product.skus;
      $[6] = product.type;
      $[7] = hasSpecificSkuMatch;
      $[8] = matchedModifierPrice;
    } else {
      hasSpecificSkuMatch = $[7];
      matchedModifierPrice = $[8];
    }
    let discountPrice;
    if (hasSpecificSkuMatch) {
      discountPrice = matchedModifierPrice ?? defaultDiscountPrice;
    } else {
      let t3;
      if ($[9] !== prices) {
        t3 = prices?.[0]?.modifiers?.length ? prices[0].modifiers.reduce(_temp3, prices[0].modifiers[0].amount) : undefined;
        $[9] = prices;
        $[10] = t3;
      } else {
        t3 = $[10];
      }
      const lowestModifierPrice = t3;
      discountPrice = lowestModifierPrice && lowestModifierPrice < defaultDiscountPrice ? lowestModifierPrice : defaultDiscountPrice;
    }
    let t3;
    if ($[11] !== discountPrice || $[12] !== originalPrice) {
      t3 = Math.round((originalPrice - discountPrice) / originalPrice * 100);
      $[11] = discountPrice;
      $[12] = originalPrice;
      $[13] = t3;
    } else {
      t3 = $[13];
    }
    t2 = t3;
  }
  const discount = t2;
  let checkoutUrl = channel === "store" ? `/${market}/checkout` : `/${market}/lp/${channel}/checkout`;
  if (itemId) {
    checkoutUrl = `${checkoutUrl}?item_id=${itemId}`;
  }
  if (mirrorUrl) {
    let t3;
    let t4;
    if ($[14] !== mirrorUrl || $[15] !== searchParams) {
      const url = new URL(mirrorUrl);
      const baseDomain = url.origin;
      const path = url.pathname;
      t3 = baseDomain;
      t4 = rebuildUrlParams(stripForcedTrackingParams(searchParams), path);
      $[14] = mirrorUrl;
      $[15] = searchParams;
      $[16] = t3;
      $[17] = t4;
    } else {
      t3 = $[16];
      t4 = $[17];
    }
    checkoutUrl = `${t3}${t4}`;
  }
  const checkoutUrl_0 = checkoutUrl;
  let t3;
  if ($[18] !== callToActionId || $[19] !== channel || $[20] !== checkoutUrl_0) {
    t3 = () => {
      trackAction("cta_click", {
        component: "BtnBuy",
        destination: checkoutUrl_0,
        channel,
        cta_id: callToActionId ?? null
      });
    };
    $[18] = callToActionId;
    $[19] = channel;
    $[20] = checkoutUrl_0;
    $[21] = t3;
  } else {
    t3 = $[21];
  }
  const handleCtaClick = t3;
  let t4;
  if ($[22] !== callToAction || $[23] !== discount || $[24] !== market) {
    t4 = getInitialCtaString(callToAction, getT(getLanguageForMarket(market), {
      buy
    })("buy.selectSizeNow"), discount, market);
    $[22] = callToAction;
    $[23] = discount;
    $[24] = market;
    $[25] = t4;
  } else {
    t4 = $[25];
  }
  const ariaLabel = t4;
  if (bigButton) {
    let t5;
    if ($[26] !== callToAction || $[27] !== discount || $[28] !== market) {
      t5 = <Cta callToAction={callToAction} discount={discount} market={market} />;
      $[26] = callToAction;
      $[27] = discount;
      $[28] = market;
      $[29] = t5;
    } else {
      t5 = $[29];
    }
    let t6;
    if ($[30] !== callToActionId || $[31] !== checkoutUrl_0 || $[32] !== handleCtaClick || $[33] !== t5) {
      t6 = <Button as="link" variant="primary" href={checkoutUrl_0} id={callToActionId} prefetch={true} data-cta="true" onClick={handleCtaClick} className="mx-auto my-4 w-full min-w-[75%] text-xl font-bold uppercase sm:w-auto sm:px-8 sm:text-2xl md:w-full lg:min-w-full xl:min-w-[75%]">{t5}</Button>;
      $[30] = callToActionId;
      $[31] = checkoutUrl_0;
      $[32] = handleCtaClick;
      $[33] = t5;
      $[34] = t6;
    } else {
      t6 = $[34];
    }
    return t6;
  }
  if (full) {
    let t5;
    if ($[35] === Symbol.for("react.memo_cache_sentinel")) {
      t5 = <ShoppingCartIcon className="block h-8 max-h-[35px] w-8 max-w-[35px]" aria-hidden="true" />;
      $[35] = t5;
    } else {
      t5 = $[35];
    }
    let t6;
    if ($[36] !== callToAction || $[37] !== discount || $[38] !== market) {
      t6 = <span data-cy="buy-button" className="w-full"><Cta callToAction={callToAction} discount={discount} market={market} /></span>;
      $[36] = callToAction;
      $[37] = discount;
      $[38] = market;
      $[39] = t6;
    } else {
      t6 = $[39];
    }
    let t7;
    if ($[40] !== callToActionId || $[41] !== checkoutUrl_0 || $[42] !== handleCtaClick || $[43] !== t6) {
      t7 = <Button as="link" variant="primary" href={checkoutUrl_0} id={callToActionId} prefetch={true} data-cta="true" onClick={handleCtaClick} className="my-2 flex w-full items-center gap-2 text-lg font-bold uppercase md:hidden">{t5}{t6}</Button>;
      $[40] = callToActionId;
      $[41] = checkoutUrl_0;
      $[42] = handleCtaClick;
      $[43] = t6;
      $[44] = t7;
    } else {
      t7 = $[44];
    }
    return t7;
  }
  let t5;
  if ($[45] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <ShoppingCartIcon className="block h-5 w-5 md:mr-2" aria-hidden="true" />;
    $[45] = t5;
  } else {
    t5 = $[45];
  }
  let t6;
  if ($[46] !== callToAction || $[47] !== discount || $[48] !== market) {
    t6 = <span data-cy="buy-button"><Cta callToAction={callToAction} discount={discount} market={market} /></span>;
    $[46] = callToAction;
    $[47] = discount;
    $[48] = market;
    $[49] = t6;
  } else {
    t6 = $[49];
  }
  let t7;
  if ($[50] !== ariaLabel || $[51] !== callToActionId || $[52] !== checkoutUrl_0 || $[53] !== handleCtaClick || $[54] !== t6) {
    t7 = <Button as="link" variant="primary" size="sm" href={checkoutUrl_0} id={callToActionId} prefetch={true} data-cta="true" onClick={handleCtaClick} aria-label={ariaLabel} className="font-display hidden font-black uppercase md:inline-flex">{t5}{t6}</Button>;
    $[50] = ariaLabel;
    $[51] = callToActionId;
    $[52] = checkoutUrl_0;
    $[53] = handleCtaClick;
    $[54] = t6;
    $[55] = t7;
  } else {
    t7 = $[55];
  }
  const t8 = callToActionId ? `${callToActionId}-mobile` : undefined;
  let t9;
  if ($[56] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <ShoppingCartIcon className="block h-8 w-8 md:mr-2 md:h-5 md:w-5" aria-hidden="true" />;
    $[56] = t9;
  } else {
    t9 = $[56];
  }
  let t10;
  if ($[57] !== ariaLabel || $[58] !== checkoutUrl_0 || $[59] !== handleCtaClick || $[60] !== t8) {
    t10 = <div className="block p-4 md:hidden"><Link href={checkoutUrl_0} id={t8} className="hover:cursor-pointer hover:no-underline" prefetch={true} data-cta="true" onClick={handleCtaClick} aria-label={ariaLabel}>{t9}</Link></div>;
    $[57] = ariaLabel;
    $[58] = checkoutUrl_0;
    $[59] = handleCtaClick;
    $[60] = t8;
    $[61] = t10;
  } else {
    t10 = $[61];
  }
  let t11;
  if ($[62] !== t10 || $[63] !== t7) {
    t11 = <div className="flex items-center p-4!">{t7}{t10}</div>;
    $[62] = t10;
    $[63] = t7;
    $[64] = t11;
  } else {
    t11 = $[64];
  }
  return t11;
};
export const BtnBuy = props => {
  const $ = _c(2);
  let t0;
  if ($[0] !== props) {
    t0 = <Suspense><BtnBuyInner {...props} /></Suspense>;
    $[0] = props;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
};
function _temp(skuList) {
  return skuList.skus;
}
function _temp2(sku_0) {
  return sku_0.isPreselected === true;
}
function _temp3(min, m) {
  return m.amount < min ? m.amount : min;
}